// \src\pages\noticias\page.tsx

import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAdmin } from "@/context/AdminContext";
import type { NewsItem } from "@/context/AdminContext";
import { useRssNews } from "@/hooks/useRssNews";
import PromoCarouselHome from "@/pages/home/components/PromoCarouselHome";

export default function Noticias() {
  const { data } = useAdmin();
  const { news, loading, error, lastUpdated, refresh } = useRssNews(data.rssSources);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const NEWS_PER_PAGE = 10;
  const totalPages = Math.max(1, Math.ceil(news.length / NEWS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * NEWS_PER_PAGE;
  const visibleNews = news.slice(startIndex, startIndex + NEWS_PER_PAGE);

  const currentSelectedNews = selectedNews
    ? news.find((item) => item.id === selectedNews.id) || selectedNews
    : null;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  };

  const formatLastUpdated = (d: Date) => {
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const showEmpty = !loading && news.length === 0;

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        <section className="bg-background-200 border-b border-background-300 py-8 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <nav className="text-xs text-foreground-400 mb-3 font-label">
              <Link to="/" className="hover:text-primary-500 cursor-pointer">Início</Link>
              <span className="mx-2">/</span>
              <span className="text-primary-500">Notícias</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">Notícias</h1>
            <p className="text-foreground-500 text-sm mt-2">As últimas notícias reais do setor de construção civil</p>
          </div>
        </section>

        <PromoCarouselHome />

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          {/* Barra de atualização automática */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
            {loading && news.length === 0 ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-loader-4-line text-primary-500 text-xl animate-spin" />
                </div>
                <div>
                  <p className="text-lg md:text-xl font-heading font-bold text-primary-700 leading-tight">
                    Buscando as notícias mais recentes.
                  </p>
                  <p className="text-sm text-foreground-500 mt-0.5 font-label">
                    As primeiras notícias aparecerão assim que cada fonte responder.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-foreground-400 font-label">
                <i className={`ri-rss-line text-primary-500 ${loading ? "animate-pulse" : ""}`} />
                {loading ? (
                  <span>Atualizando notícias em segundo plano...</span>
                ) : lastUpdated ? (
                  <span>Atualizado automaticamente às {formatLastUpdated(lastUpdated)}</span>
                ) : (
                  <span>Notícias do setor de construção civil</span>
                )}
              </div>
            )}
            <button
              onClick={refresh}
              disabled={loading}
              className="flex items-center gap-1.5 bg-background-100 border border-background-300 hover:border-primary-300 hover:text-primary-600 text-foreground-600 px-3 py-1.5 rounded-full text-xs font-label font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} /> Atualizar
            </button>
          </div>

          {/* Estado de erro quando nada carrega */}
          {error && news.length === 0 && (
            <div className="bg-background-100 border border-background-200 rounded-lg p-10 text-center mb-8">
              <i className="ri-wifi-off-line text-3xl text-foreground-300 mb-3 block" />
              <p className="text-foreground-600 text-sm font-label mb-3">{error}</p>
              <button onClick={refresh} className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-refresh-line" /> Tentar novamente
              </button>
            </div>
          )}

          {/* Estado vazio (nenhuma fonte retornou notícias) */}
          {showEmpty && !error && (
            <div className="bg-background-100 border border-background-200 rounded-lg p-10 text-center mb-8">
              <i className="ri-rss-line text-3xl text-foreground-300 mb-3 block" />
              <p className="text-foreground-600 text-sm font-label mb-1">Nenhuma notícia foi carregada no momento.</p>
              <p className="text-foreground-400 text-xs font-label mb-4">Confira as fontes de notícias (RSS) no painel administrativo ou tente atualizar.</p>
              <button onClick={refresh} className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-refresh-line" /> Atualizar agora
              </button>
            </div>
          )}

          {/* Skeleton de carregamento */}
          {loading && news.length === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                <div
                  key={i}
                  className="border border-background-200 bg-background-100 rounded-lg p-5 animate-pulse"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-5 w-24 bg-background-200 rounded-full" />
                    <div className="h-3 w-20 bg-background-200 rounded" />
                  </div>
                  <div className="h-5 bg-background-200 rounded w-4/5 mb-3" />
                  <div className="h-3 bg-background-200 rounded w-full mb-2" />
                  <div className="h-3 bg-background-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          )}

          {/* Grid de notícias reais — 10 por página, até 30 no total */}
          {news.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {visibleNews.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedNews(item)}
                    className="group w-full h-full bg-background-50 border border-background-200 rounded-lg p-5 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer text-left flex flex-col"
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1.5 bg-primary-100 text-primary-700 text-xs font-label font-bold px-2.5 py-1 rounded-full">
                        <i className="ri-newspaper-line" />
                        {item.source}
                      </span>

                      <span className="text-foreground-400 text-xs font-label">
                        <i className="ri-calendar-line mr-1" />
                        {formatDate(item.date)}
                      </span>
                    </div>

                    <h3 className="font-heading font-bold text-foreground-950 text-base md:text-lg leading-snug mb-2 line-clamp-3 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h3>

                    {item.excerpt && (
                      <p className="text-foreground-600 text-sm leading-relaxed line-clamp-3 mb-4">
                        {item.excerpt}
                      </p>
                    )}

                    <div className="mt-auto pt-3 border-t border-background-200 flex items-center justify-between gap-3 text-xs font-label">
                      <span className="text-foreground-400">
                        <i className="ri-time-line mr-1" />
                        {item.readTime}
                      </span>

                      <span className="inline-flex items-center gap-1.5 text-primary-600 font-semibold">
                        Ver notícia
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={safeCurrentPage === 1}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-300 bg-background-50 text-foreground-600 text-xs font-label font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-left-line" />
                    Anterior
                  </button>

                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;

                    return (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`w-9 h-9 rounded-md border text-xs font-label font-bold transition-colors cursor-pointer ${
                          safeCurrentPage === pageNumber
                            ? "bg-primary-500 border-primary-500 text-background-50"
                            : "bg-background-50 border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={safeCurrentPage === totalPages}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-300 bg-background-50 text-foreground-600 text-xs font-label font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Próxima
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      {currentSelectedNews && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelectedNews(null)}>
          <div className="relative bg-background-50 rounded-lg max-w-2xl w-full my-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedNews(null)}
              className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full cursor-pointer transition-colors"
              aria-label="Fechar notícia"
            >
              <i className="ri-close-line text-lg" />
            </button>
            <div className="p-6 md:p-7 pr-16">
              <span className="inline-block bg-primary-100 text-primary-700 text-xs font-label font-bold px-3 py-1 rounded-full mb-3">{currentSelectedNews.source}</span>
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-3 leading-tight">{currentSelectedNews.title}</h2>
              <div className="flex items-center gap-3 text-foreground-400 text-xs font-label mb-4 flex-wrap">
                <span><i className="ri-calendar-line mr-1" />{formatDate(currentSelectedNews.date)}</span>
                <span><i className="ri-time-line mr-1" />{currentSelectedNews.readTime} de leitura</span>
                <span><i className="ri-newspaper-line mr-1" />{currentSelectedNews.source}</span>
              </div>
              <p className="text-foreground-600 text-sm leading-relaxed">{currentSelectedNews.excerpt}</p>
              {currentSelectedNews.link ? (
                <div className="mt-6 pt-4 border-t border-background-200">
                  <p className="text-xs text-foreground-400 mb-3">Para a notícia completa, acesse a fonte original: <strong>{currentSelectedNews.source}</strong></p>
                  <a href={currentSelectedNews.link} target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-md text-sm font-label font-semibold transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-external-link-line" /> Ler notícia completa
                  </a>
                </div>
              ) : (
                <div className="mt-6 pt-4 border-t border-background-200">
                  <p className="text-xs text-foreground-400">Para a notícia completa, consulte a fonte original: <strong>{currentSelectedNews.source}</strong></p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}